import operationsPalettes from '@core/constants/palettes';
import { ColorsEntity } from '@core/models/user';
import { PrescriptionZone } from '@screens/Prescriptions/utils/prescriptions.models';

/**
 * This method pick a color for the zone based on the number of zones.
 * Case the zone already has a color, it own color is returned instead.
 * @param zone Feature<Polygon | MultiPolygon>
 * @param index Index of the zone in the zones list
 * @param arr Zones list
 * @returns An hexadecimal color or "transparent" case no palette matches.
 */
export function getZoneColor(
  zone: PrescriptionZone,
  index: number,
  arr: PrescriptionZone[]
): string {
  if (zone.properties.color) return zone.properties.color;

  const palette = pickPaletteFromNumberOfZones(arr.length);

  if (palette) {
    const { colors } = palette;

    const limit = colors.length;
    const dnIndex = Number.isFinite(zone.properties.DN) ? Number(zone.properties.DN) - 1 : null;
    const key = dnIndex !== null ? ((dnIndex % limit) + limit) % limit : index % limit;
    const color = colors[key].color;

    return color;
  }

  return 'transparent';
}

/**
 * Pick a color palette based on the number of zones.
 * @param numberOfZones Amount of zones
 * @returns An color palette.
 */
export function pickPaletteFromNumberOfZones(numberOfZones: number) {
  if (numberOfZones < 4) {
    return operationsPalettes.find((palette) => palette.name === 'SMS 3');
  }

  const paletteLabel = `SMS ${numberOfZones}`;

  const findPalette = operationsPalettes.find((palette) => palette.name === paletteLabel);

  if (findPalette) return findPalette;

  return operationsPalettes.find((palette) => palette.name === 'SMS 10');
}

export function getDnIndexMapFromZones(
  zones: Array<{ properties?: { DN?: number } }>
): Map<number, number> {
  const dnValues = Array.from(
    new Set(
      zones
        .map((zone) => zone?.properties?.DN)
        .filter((dn): dn is number => Number.isFinite(dn as number))
        .map(Number)
    )
  ).sort((a, b) => a - b);

  return new Map(dnValues.map((dn, index) => [dn, index]));
}

export function getDnRemapFromZones(
  zones: Array<{ properties?: { DN?: number } }>
): Map<number, number> {
  const dnIndexMap = getDnIndexMapFromZones(zones);
  return new Map(Array.from(dnIndexMap.entries(), ([dn, index]) => [dn, index + 1]));
}

export const normalizeColorPalette = (colors: ReadonlyArray<ColorsEntity>) => {
  let wasNormalized = false;
  return colors.reduce<ReadonlyArray<ColorsEntity>>((acc, color, i) => {
    if (i === 0 && colors.length > 1 && color.start === -1 && color.end === 0) {
      wasNormalized = true;
      return [
        {
          start: color.start,
          end: colors[i + 1].end,
          startp: color.rate ?? 0,
          endp: color.rate ?? 0,
        },
      ];
    } else if (wasNormalized && i === 1) {
      return acc;
    }
    return [
      ...acc,
      {
        start: color.start,
        end: color.end,
        startp: (color.rate ?? 0) - (wasNormalized ? 1 : 0),
        endp: (color.rate ?? 0) - (wasNormalized ? 1 : 0),
      },
    ];
  }, []);
};
